import type { EnrollmentMode } from '@repo/shared-types';
import { currentMonth } from './sessionTime';
import { normalizeForSearch } from './textSearch';

/**
 * Everything that narrows the month grid, in one shape.
 *
 * Every field is optional and an absent/empty one means "no narrowing" — the
 * Chốt sổ embed passes no filter at all and must behave exactly as it did
 * before this module existed.
 */
export interface AttendanceMonthFilter {
  /** Subject ids to keep. Empty/absent = every subject. */
  subjectIds?: string[];
  /** Hình thức học to keep. Empty/absent = every mode. */
  modes?: EnrollmentMode[];
  /** Free text over name + nickname, diacritic-insensitive. */
  q?: string;
  /** Narrow to a single student row — ignored when no row matches it. */
  studentId?: string | null;
}

/** The only session fields the pre-pivot filters read. Structural on purpose:
 *  callers pass real `SessionWithDetail`s, tests pass two-field fixtures. */
export interface FilterableSession {
  subject?: { id: string } | null;
  participants: { mode?: EnrollmentMode }[];
}

/** The only row fields the post-pivot filters read — one `students` entry
 *  produced by `pivotAttendanceGrid`. */
export interface FilterableRow {
  studentId: string;
  studentName: string;
  studentNickname: string | null;
}

const YM_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * `?month=` -> a 'YYYY-MM' safe to hand to `monthRange`.
 *
 * Anything absent, malformed, or outside 01..12 falls back to the current
 * calendar month. "Current" is read with UTC getters (`sessionTime.currentMonth`)
 * like every other today/this-week/this-month site in the app: lesson times are
 * wall-clock stored as UTC-naive and `monthRange` returns UTC bounds, so a
 * locally-computed month could ask for a window the data does not live in.
 */
export function parseMonthParam(raw: string | null | undefined, now: Date): string {
  return raw && YM_RE.test(raw) ? raw : currentMonth(now);
}

/**
 * Subject + mode, applied BEFORE the pivot.
 *
 * The two do different things on purpose. Subject drops whole sessions, so an
 * unselected subject's date column disappears from the grid. Mode drops
 * participants inside the surviving sessions, so a student who does not pay
 * that way loses their marks and — once every session of theirs is emptied —
 * their row. A session left with nobody in it is dropped too: keeping it would
 * render a date header whose every cell is a dash.
 *
 * Returns the input array itself when nothing is selected, so the Chốt sổ
 * embed's `useMemo` over the pivot is not invalidated by this call.
 */
export function filterSessionsForGrid<T extends FilterableSession>(
  sessions: T[],
  filter?: AttendanceMonthFilter,
): T[] {
  const subjectIds = filter?.subjectIds ?? [];
  const modes = filter?.modes ?? [];
  if (subjectIds.length === 0 && modes.length === 0) return sessions;

  const out: T[] = [];
  for (const s of sessions) {
    if (subjectIds.length > 0 && !(s.subject && subjectIds.includes(s.subject.id))) continue;
    if (modes.length === 0) {
      out.push(s);
      continue;
    }
    const participants = s.participants.filter((x) => !!x.mode && modes.includes(x.mode));
    if (participants.length === 0) continue;
    // Copy, never splice: the sessions array belongs to the react-query cache.
    // The cast is required and safe: reading `.participants` off a `T` yields
    // the CONSTRAINT's element type, so the spread would otherwise widen a
    // `SessionWithDetail` down to `{ mode?: EnrollmentMode }[]`. Every runtime
    // value in `participants` is still a member of `T`'s own array.
    out.push({ ...s, participants } as T);
  }
  return out;
}

/**
 * Search + single-student, applied AFTER the pivot.
 *
 * `studentId` narrows to one row, but ONLY when that row exists. A link
 * carrying a student who has no session this month — or who no longer exists —
 * must show the month rather than an empty screen that reads like a bug; the
 * design says an unknown id is ignored.
 *
 * Search runs second and on the already-narrowed set, so a `?student=` link
 * plus a typed query behaves like two filters and not like a reset.
 */
export function filterGridRows<R extends FilterableRow>(
  rows: R[],
  filter?: AttendanceMonthFilter,
): R[] {
  let out = rows;
  const studentId = filter?.studentId;
  if (studentId) {
    const only = out.filter((r) => r.studentId === studentId);
    if (only.length > 0) out = only;
  }
  const q = normalizeForSearch((filter?.q ?? '').trim());
  if (q) {
    out = out.filter(
      (r) =>
        normalizeForSearch(r.studentName).includes(q) ||
        (!!r.studentNickname && normalizeForSearch(r.studentNickname).includes(q)),
    );
  }
  return out;
}

/**
 * Did the teacher narrow anything? Decides which empty story an empty grid
 * tells: "nothing matches, here is the way out" versus "this month has no
 * sessions at all". `studentId` counts — see `filterGridRows`.
 */
export function isAttendanceFilterActive(filter?: AttendanceMonthFilter): boolean {
  return (
    (filter?.subjectIds?.length ?? 0) > 0 ||
    (filter?.modes?.length ?? 0) > 0 ||
    !!filter?.q?.trim() ||
    !!filter?.studentId
  );
}
